import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;

const formatTotal = (minutes) => {
  const h = Math.floor((minutes || 0) / 60);
  const m = (minutes || 0) % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatWorkDate = (iso) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

// Manual time entries — separate from the live timer & site logs, never
// feeds invoices or metrics. All records soft-delete with a full audit trail.
export const JobTimePanel = ({ jobId, getAuthHeaders }) => {
  const [entries, setEntries] = useState([]);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // form state (add or edit)
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // entry being edited, or null
  const [workDate, setWorkDate] = useState(todayISO());
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");

  // delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(null);

  const headers = useCallback(
    () => ({ ...getAuthHeaders(), "Content-Type": "application/json" }),
    [getAuthHeaders]
  );

  const fetchEntries = useCallback(async () => {
    try {
      const res = await fetch(`${API}/site-mode/job/${jobId}/time-entries`, {
        headers: getAuthHeaders(),
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEntries(data.entries || []);
      setTotalMinutes(data.total_minutes || 0);
    } catch {
      toast.error("Could not load time entries");
    } finally {
      setLoading(false);
    }
  }, [jobId, getAuthHeaders]);

  useEffect(() => {
    setLoading(true);
    fetchEntries();
  }, [fetchEntries]);

  const openAdd = () => {
    setEditing(null);
    setWorkDate(todayISO());
    setHours("");
    setMinutes("");
    setNote("");
    setFormOpen(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setWorkDate(entry.work_date);
    setHours(String(Math.floor(entry.duration_minutes / 60)));
    setMinutes(String(entry.duration_minutes % 60));
    setNote(entry.note || "");
    setFormOpen(true);
  };

  const durationMinutes = () => {
    const h = parseInt(hours || "0", 10);
    const m = parseInt(minutes || "0", 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return h * 60 + m;
  };

  const save = async () => {
    const duration = durationMinutes();
    if (duration <= 0) {
      toast.error("Enter a duration greater than zero");
      return;
    }
    if (duration > 1440) {
      toast.error("A single entry cannot exceed 24 hours");
      return;
    }
    setSaving(true);
    try {
      const res = editing
        ? await fetch(`${API}/site-mode/time-entries/${editing.id}`, {
            method: "PATCH",
            headers: headers(),
            credentials: "same-origin",
            body: JSON.stringify({
              version: editing.version,
              work_date: workDate,
              duration_minutes: duration,
              note: note,
            }),
          })
        : await fetch(`${API}/site-mode/time-entries`, {
            method: "POST",
            headers: headers(),
            credentials: "same-origin",
            body: JSON.stringify({
              enquiry_id: jobId,
              work_date: workDate,
              duration_minutes: duration,
              note: note.trim() || null,
            }),
          });
      if (res.status === 409) {
        toast.error("This entry was changed by someone else — refreshing");
        await fetchEntries();
        setFormOpen(false);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success(editing ? "Time entry updated" : "Time entry added");
      setFormOpen(false);
      await fetchEntries();
    } catch (err) {
      toast.error(err.message || "Could not save time entry");
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/site-mode/time-entries/${confirmDelete.id}`, {
        method: "DELETE",
        headers: headers(),
        credentials: "same-origin",
        body: JSON.stringify({ version: confirmDelete.version }),
      });
      if (res.status === 409) {
        toast.error("This entry was changed by someone else — refreshing");
      } else if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      } else {
        toast.success("Time entry removed");
      }
      setConfirmDelete(null);
      await fetchEntries();
    } catch {
      toast.error("Could not remove time entry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="job-time-panel">
      <CardHeader className="p-4 pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
            Manual Time
          </CardTitle>
          <span className="text-lg font-bold" data-testid="time-entries-total">
            {formatTotal(totalMinutes)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Logged work time for this job. Does not affect invoices or metrics.
        </p>
      </CardHeader>
      <CardContent className="p-4 pt-2 space-y-3">
        <Button
          onClick={openAdd}
          variant="outline"
          className="w-full h-11 gap-2"
          data-testid="add-time-entry-btn"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add time
        </Button>

        {loading ? (
          <div className="flex justify-center py-3">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading time entries" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-2" data-testid="time-entries-empty">
            No time recorded yet.
          </p>
        ) : (
          <ul className="space-y-2 max-h-64 overflow-y-auto" data-testid="time-entries-list" aria-label="Time entries">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start gap-3 p-2 rounded-lg bg-muted/30 text-sm"
                data-testid={`time-entry-${entry.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium">
                    {formatWorkDate(entry.work_date)} · {formatTotal(entry.duration_minutes)}
                  </p>
                  {entry.note && <p className="text-xs text-muted-foreground break-words">{entry.note}</p>}
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {entry.created_by?.name || entry.created_by?.email}
                    {entry.updated_at && " · edited"}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-9 w-9 p-0"
                    onClick={() => openEdit(entry)}
                    aria-label={`Edit time entry, ${formatWorkDate(entry.work_date)}, ${formatTotal(entry.duration_minutes)}`}
                    data-testid={`edit-time-entry-${entry.id}`}
                  >
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-9 w-9 p-0 text-red-500 hover:text-red-700"
                    onClick={() => setConfirmDelete(entry)}
                    aria-label={`Remove time entry, ${formatWorkDate(entry.work_date)}, ${formatTotal(entry.duration_minutes)}`}
                    data-testid={`delete-time-entry-${entry.id}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit time entry" : "Add time"}</DialogTitle>
            <DialogDescription>
              Record the date worked and how long it took.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="time-entry-date">Work date</Label>
              <Input
                id="time-entry-date"
                type="date"
                value={workDate}
                max={todayISO()}
                onChange={(e) => setWorkDate(e.target.value)}
                className="h-11"
                data-testid="time-entry-date-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="time-entry-hours">Hours</Label>
                <Input
                  id="time-entry-hours"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="24"
                  placeholder="0"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  className="h-11"
                  data-testid="time-entry-hours-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="time-entry-minutes">Minutes</Label>
                <Input
                  id="time-entry-minutes"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="59"
                  placeholder="0"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  className="h-11"
                  data-testid="time-entry-minutes-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="time-entry-note">Note (optional)</Label>
              <Input
                id="time-entry-note"
                maxLength={500}
                placeholder="e.g. Second fix carpentry"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="h-11"
                data-testid="time-entry-note-input"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1 h-11" onClick={() => setFormOpen(false)} data-testid="time-entry-cancel-btn">
                Cancel
              </Button>
              <Button className="flex-1 h-11 gap-2" onClick={save} disabled={saving} data-testid="time-entry-save-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {editing ? "Save changes" : "Add entry"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove time entry?</DialogTitle>
            <DialogDescription>
              {confirmDelete &&
                `${formatWorkDate(confirmDelete.work_date)} · ${formatTotal(confirmDelete.duration_minutes)} will be removed from the total. A record is kept for auditing.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 h-11" onClick={() => setConfirmDelete(null)} data-testid="confirm-delete-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1 h-11"
              onClick={removeEntry}
              disabled={saving}
              data-testid="confirm-delete-btn"
            >
              Remove
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
